/**
 * Memory Consolidator Connector — Mycelium → memory-consolidator edge fn
 *
 * Subscribes to `memory:consolidate_requested` events and invokes the
 * `memory-consolidator` Supabase edge function which synthesizes a child
 * scope's `unified_memory` rows into a single parent-scope summary row
 * with `is_consolidated = TRUE`.
 *
 * Event payload contract:
 *   {
 *     principal_id:       string,       // UUID, mycelium_principals.id
 *     scope_kind:         string,       // child scope being rolled up
 *     scope_id:           string|null,
 *     parent_scope_kind:  string,
 *     parent_scope_id:    string|null,
 *     user_id:            string,       // FK target on unified_memory
 *     org_id?:            string|null,
 *     max_rows?:          number,
 *     dry_run?:           boolean,
 *   }
 *
 * Failures are logged but never rethrown — consolidation is non-blocking.
 *
 * @module mycelium-memory-consolidator
 * @since 2026-05-08
 * @see supabase/functions/memory-consolidator/index.ts
 * @see docs/protocols/mycelium/SYNAPTIC_AWAKENING.md
 */

import {
  ConsolidateMemoryPayloadSchema,
  type ConsolidateMemoryPayload,
} from "@flowstate/mycelium-core";
import {
  myceliumEventBackbone,
  type MyceliumEvent,
} from "./mycelium-event-backbone.service";
import { logger } from "../lib/logger";

const TRIGGER_DOMAIN = "memory" as const;
const TRIGGER_TYPE = "consolidate_requested" as const;

export class MemoryConsolidatorConnector {
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("MemoryConsolidatorConnector already initialized");
      return;
    }

    logger.info("🧠 Memory Consolidator connector initializing");

    myceliumEventBackbone.subscribe({
      name: "MemoryConsolidatorConnector",
      domains: [TRIGGER_DOMAIN],
      handler: this.handleEvent.bind(this),
      priority: 5,
    });

    this.initialized = true;
    logger.info("✅ Memory Consolidator connector ready");
  }

  private async handleEvent(event: MyceliumEvent): Promise<void> {
    if (event.type !== TRIGGER_TYPE) return;

    const parsed = ConsolidateMemoryPayloadSchema.safeParse(event.payload ?? {});
    if (!parsed.success) {
      logger.warn("MemoryConsolidator: invalid event payload", {
        eventId: event.id,
        issues: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`),
      });
      return;
    }
    const payload: ConsolidateMemoryPayload = parsed.data;

    const supabaseUrl = process.env.SUPABASE_URL ?? "";
    const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY ?? "";
    if (!supabaseUrl || !serviceRoleKey) {
      logger.warn("MemoryConsolidator: missing SUPABASE_URL / SERVICE_ROLE_KEY");
      return;
    }

    try {
      const r = await fetch(`${supabaseUrl}/functions/v1/memory-consolidator`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
          apikey: serviceRoleKey,
        },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        const body = await r.text();
        logger.warn("MemoryConsolidator: edge fn returned error", {
          eventId: event.id,
          status: r.status,
          body: body.slice(0, 500),
        });
        return;
      }

      const result = (await r.json()) as { promise?: { id: string }; started?: boolean };
      logger.info("MemoryConsolidator: started durable consolidation", {
        eventId: event.id,
        principal: payload.principal_id,
        scope: `${payload.scope_kind} → ${payload.parent_scope_kind}`,
        promiseId: result.promise?.id,
      });
    } catch (err) {
      logger.error("MemoryConsolidator: invocation threw", {
        eventId: event.id,
        error: err instanceof Error ? err.message : String(err),
      });
      // Don't rethrow — consolidation is non-blocking.
    }
  }

  async dispose(): Promise<void> {
    myceliumEventBackbone.unsubscribe("MemoryConsolidatorConnector");
    this.initialized = false;
  }
}

export const memoryConsolidatorConnector = new MemoryConsolidatorConnector();
